import { Command } from 'commander';

import { graphql, GraphQLRequestError } from '../graphql/client.ts';
import { Me } from '../graphql/operations/common.ts';
import { loadApplicationSession } from '../lib/application-session.ts';
import { loadElevatedCredentials, storedUserAccessToken } from '../lib/auth.ts';
import { applicationIdentity, clientCredentials, fetchClientCredentialsToken } from '../lib/client-credentials.ts';
import { error, hint, info, isJsonMode, printJson } from '../lib/output.ts';
import { formatResult } from '../lib/render.ts';
import { grantedScopes } from '../lib/token-claims.ts';

export function apiScopes(accessToken: string | null): string[] {
  return accessToken ? grantedScopes(accessToken).filter((scope) => scope.includes('/')) : [];
}

function currentElevation(): { level: string; until: string } | null {
  const elevated = loadElevatedCredentials();

  return elevated ? { level: elevated.level, until: new Date(elevated.expiresAt * 1000).toISOString() } : null;
}

export const whoamiCommand = new Command('whoami')
  .summary('Show what the CLI is currently acting as')
  .description(
    'Show what the CLI is currently acting as\n\n' +
      'Reports a person or an application, matching what every command\n' +
      'authenticates as. Use --json for machine-readable output.\n\n' +
      'Examples:\n' +
      '  sl whoami                   Show the current identity\n' +
      '  sl whoami --json            Output the full object as JSON',
  )
  .action(async function (this: Command) {
    const opts = this.optsWithGlobals();

    // An application cannot be identified by asking the API: Hermes refuses its
    // own tokens on the management API, so `me` returns null. The claims of the
    // token we just minted are the only answer available.
    const session = loadApplicationSession();
    if (session) {
      const identity = applicationIdentity(session.accessToken);
      if (isJsonMode(opts)) {
        printJson({ kind: 'application', via: 'session', ...identity });
        return;
      }

      info('Acting as an application.\n');
      info(
        formatResult({
          application: session.applicationId,
          'acts for company': identity?.companyId,
          subject: identity?.sub,
          'token expires': new Date(session.expiresAt).toISOString(),
          source: 'sl use application — its credential is already revoked',
        }) ?? '',
      );
      hint('Run `sl use me` to act as yourself again.');
      return;
    }

    const machine = clientCredentials();
    if (machine) {
      let identity;
      try {
        identity = applicationIdentity(await fetchClientCredentialsToken(machine));
      } catch (err) {
        error((err as Error).message);
        hint('Check SORTLIST_CLIENT_ID, SORTLIST_CLIENT_SECRET and the token endpoint.');
        process.exit(2);
      }
      if (!identity) {
        error('The token endpoint returned something this CLI could not read.');
        process.exit(1);
      }

      if (isJsonMode(opts)) {
        printJson({ kind: 'application', clientId: machine.clientId, ...identity });
        return;
      }

      info('Acting as an application.\n');
      info(
        formatResult({
          application: identity.applicationId,
          'acts for company': identity.companyId,
          subject: identity.sub,
          credential: `${identity.credentialId} (${machine.clientId})`,
          'token expires': identity.expiresAt.toISOString(),
          source: 'SORTLIST_CLIENT_ID / SORTLIST_CLIENT_SECRET',
        }) ?? '',
      );
      hint('A credential carries no scopes, so scoped data stays out of reach.');
      hint('Unset both variables to act as yourself again.');
      return;
    }

    let data;
    try {
      data = await graphql(Me, undefined, {
        endpoint: opts.endpoint,
        throwOnError: true,
      });
    } catch (err) {
      if (err instanceof GraphQLRequestError) {
        error('Could not verify your identity — the API returned an error:');
        const messages = err.graphqlErrors?.map((e) => e.message) ?? [err.message];
        for (const message of messages) info(`  ${message}`);
        hint('This is a server-side or authorization error, not necessarily an expired session.');
        hint('Retry in a moment; if it persists, re-run `sl login` or report it.');
        process.exit(1);
      }
      throw err;
    }

    const user = data.me;
    if (!user) {
      // A successful `me` for a logged-in user is never null — the resolver
      // returns the calling user. Null here means the backend could not resolve
      // the account (e.g. an upstream identity/profile lookup failed), not that
      // the local session is invalid, so we do not tell the user to log in again.
      error('The API did not return your user identity.');
      hint('This is usually a temporary backend issue rather than an expired session.');
      hint('Retry in a moment; if it persists, re-run `sl login` or report it.');
      process.exit(1);
    }

    const elevation = currentElevation();
    const scopes = apiScopes(storedUserAccessToken());

    if (isJsonMode(opts)) {
      printJson({ kind: 'person', ...user, elevation, scopes });
      return;
    }

    info(elevation ? `Acting as yourself, elevated to ${elevation.level}.\n` : 'Acting as yourself.\n');
    info(
      formatResult({
        name: [user.firstName, user.lastName].filter(Boolean).join(' ') || null,
        email: user.email,
        id: user.id,
        ...(elevation && { 'elevated until': elevation.until }),
        scopes: scopes.length > 0 ? scopes.join(' ') : 'none',
      }) ?? '',
    );
    if (elevation) hint('Run `sl logout --elevated` to drop the elevation now.');
  });
